// ─── Render Parity Banner ─────────────────────────────────────
// Lightweight QA banner shown after Save when the off-screen
// parity check finds drift between the editor canvas and the
// public preview render. Designed to FEEL like a helpful signal,
// not an error. Dismissible. Click "View details" → opens a
// minimal modal listing the drifted elements with direction +
// magnitude.

import React, { useState } from 'react';
import { AlertTriangle, X, ChevronRight } from 'lucide-react';

function friendlyType(type) {
  if (!type) return 'element';
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function describeDrift(d) {
  if (!d) return null;
  const parts = [];
  // Prioritize vertical drift (the most common parity bug in our app).
  if (Math.abs(d.topPx) >= 8) {
    const dir = d.topPx > 0 ? 'lower' : 'higher';
    parts.push(`renders ${Math.abs(d.topPx)}px ${dir} on phone`);
  } else if (Math.abs(d.leftPx) >= 8) {
    const dir = d.leftPx > 0 ? 'more left' : 'more right';
    parts.push(`renders ${Math.abs(d.leftPx)}px ${dir} on phone`);
  }
  if (Math.abs(d.widthPx) >= 8) {
    const pct = Math.abs(Math.round(d.widthPct * 100));
    parts.push(`width ${pct}% off`);
  }
  if (Math.abs(d.heightPx) >= 8) {
    const pct = Math.abs(Math.round(d.heightPct * 100));
    parts.push(`height ${pct}% off`);
  }
  return parts.join(' · ');
}

export default function RenderParityBanner({ result, onDismiss, onSelectElement, onRunMobileCheck }) {
  const [open, setOpen] = useState(false);
  if (!result || result.matched || !Array.isArray(result.issues) || result.issues.length === 0) {
    return null;
  }
  const n = result.issues.length;
  const first = result.issues[0];
  const summary = n === 1
    ? `1 element renders differently on phone — ${friendlyType(first.type)}${first.name ? ` "${first.name}"` : ''} ${describeDrift(first.deltas) || ''}`.trim()
    : `${n} elements render differently on phone preview`;

  return (
    <>
      <div
        className="fixed bottom-4 right-4 z-50 w-[360px] rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 shadow-lg overflow-hidden"
        data-testid="render-parity-banner"
        role="status"
      >
        <div className="flex items-start gap-2.5 px-3.5 py-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-medium text-amber-900 dark:text-amber-100 leading-snug">
              Preview may differ on phone
            </p>
            <p className="text-[11px] text-amber-800/85 dark:text-amber-200/80 mt-0.5 leading-snug">
              {summary}
            </p>
            <div className="flex items-center gap-3 mt-2">
              <button
                onClick={() => setOpen(true)}
                className="text-[11px] font-medium text-amber-700 dark:text-amber-300 hover:underline"
                data-testid="parity-view-details"
              >
                View details
              </button>
              {onRunMobileCheck && (
                <button
                  onClick={onRunMobileCheck}
                  className="text-[11px] text-amber-700/80 dark:text-amber-300/80 hover:underline"
                  data-testid="parity-run-mobile-check"
                >
                  Run mobile check
                </button>
              )}
            </div>
          </div>
          <button
            onClick={onDismiss}
            className="text-amber-700/70 dark:text-amber-300/70 hover:text-amber-900 dark:hover:text-amber-100 transition-colors"
            aria-label="Dismiss"
            data-testid="parity-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setOpen(false)}
          data-testid="parity-details-modal"
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">Render parity</h3>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                  {result.editorCount} elements in editor · {result.publicCount} in public preview · {n} drifted
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                data-testid="parity-details-close"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
              {result.issues.map((iss) => (
                <button
                  key={`${iss.sectionId}:${iss.id}`}
                  onClick={() => {
                    setOpen(false);
                    if (onSelectElement && iss.sectionId) {
                      onSelectElement(iss.sectionId, iss.id);
                    }
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2"
                  data-testid={`parity-row-${iss.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-medium text-gray-800 dark:text-gray-100 truncate">
                      {friendlyType(iss.type)}{iss.name ? ` — ${iss.name}` : ''}
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                      {iss.kind === 'missing_in_public'
                        ? 'Visible in editor but not in public preview (unsaved?)'
                        : describeDrift(iss.deltas) || 'minor drift'}
                    </p>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                </button>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-900/60">
              <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
                The parity guard compares element positions between the editor canvas and an off-screen public render. It flags drifts greater than 2% AND 8px. Nothing has been changed automatically.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
